/**
 * Home screen layout switch and card band preferences.
 *
 * The state-first home screen (see pipar-home-ui-plan.md) is a prototype that
 * lives alongside the classic tile landing page. The choice is per browser:
 * `?home=new` / `?home=classic` in the URL overrides and persists it.
 */

export type HomeLayout = 'classic' | 'state'

const LAYOUT_KEY = 'pipar_home_layout'
const CARDS_KEY = 'pipar_home_cards'

/** The state-first home is disabled: everyone gets the classic landing page,
 *  whatever the URL or a stored choice says, and the "Try new home" switch is
 *  hidden. Flip to true to bring the prototype back. */
export const STATE_HOME_ENABLED = false

export function getHomeLayout(): HomeLayout {
  if (!STATE_HOME_ENABLED) return 'classic'
  try {
    const q = new URLSearchParams(window.location.search).get('home')
    if (q === 'new' || q === 'state') {
      localStorage.setItem(LAYOUT_KEY, 'state')
      return 'state'
    }
    if (q === 'classic') {
      localStorage.setItem(LAYOUT_KEY, 'classic')
      return 'classic'
    }
    return localStorage.getItem(LAYOUT_KEY) === 'state' ? 'state' : 'classic'
  } catch {
    return 'classic'
  }
}

export function setHomeLayout(layout: HomeLayout): void {
  try { localStorage.setItem(LAYOUT_KEY, layout) } catch { /* storage unavailable */ }
}

export type CardId = 'today' | 'calendar' | 'reminders' | 'quick' | 'notes' | 'chats'
export type CardState = 'hidden' | 'collapsed' | 'expanded'
export interface CardPref { card_id: CardId; state: CardState }

/** Defaults are decided independently of customisation — keep this list short. */
export const DEFAULT_CARDS: CardPref[] = [
  { card_id: 'today', state: 'expanded' },
  { card_id: 'calendar', state: 'expanded' },
  { card_id: 'reminders', state: 'expanded' },
  // Quick answers: header + count by default; the full list opens in a sheet.
  { card_id: 'quick', state: 'collapsed' },
  { card_id: 'notes', state: 'hidden' },
  { card_id: 'chats', state: 'hidden' },
]

/** Hard cap of items per expanded card, regardless of settings. */
export const CARD_ITEM_CAP = 3

export function loadCardPrefs(): CardPref[] {
  try {
    const raw = localStorage.getItem(CARDS_KEY)
    if (!raw) return DEFAULT_CARDS
    const stored = JSON.parse(raw) as CardPref[]
    const known = new Set(DEFAULT_CARDS.map((c) => c.card_id))
    const valid = Array.isArray(stored)
      ? stored.filter((c) => known.has(c.card_id) && ['hidden', 'collapsed', 'expanded'].includes(c.state))
      : []
    // Cards added after the prefs were saved get appended with their default state.
    const seen = new Set(valid.map((c) => c.card_id))
    return [...valid, ...DEFAULT_CARDS.filter((c) => !seen.has(c.card_id))]
  } catch {
    return DEFAULT_CARDS
  }
}

export function saveCardPrefs(prefs: CardPref[]): void {
  try { localStorage.setItem(CARDS_KEY, JSON.stringify(prefs)) } catch { /* storage unavailable */ }
}
